import React, {Component} from 'react';
import './styles.css';
import MapRenderer from './map/MapRenderer';
import GameConfig from '../game/GameConfig';
import * as PIXI from 'pixi.js';
import {Container, SystemRenderer, Point} from 'pixi.js';
import State from '../game/states/State';
import HudRenderer from './HudRenderer';
import GameModel from 'src/game/GameModel';
import {ActiveGameProps} from 'src/AppTypes';
import {Game} from 'src/game/Game';
import {pointerRenderer} from './PointerRenderer';
import * as Viewport from 'pixi-viewport';
import BuildState from 'src/game/states/BuildState';
import {Location} from '../common/map/Location';
import {buildabilityRenderer} from './map/BuildabilityRenderer';

interface RendererWithPlugins {
    plugins: PIXI.RendererPlugins;
}

type Props = ActiveGameProps & {onReturnToLobby: () => void};
export interface PixiState {
    gameState: Game;
    isAskingToSpectate: boolean;
    hasAskedToSpectate: boolean;
    isBuilding: boolean;
}

class PixiCanvas extends Component<Props, PixiState> {
    private stage: Container;
    private renderer: SystemRenderer;
    private gameCanvas?: HTMLDivElement;
    public state: PixiState;
    public pixiIsSetup: boolean = false;
    public viewport: Viewport;
    public app: PIXI.Application;

    public constructor(props: Props) {
        super(props);
        this.animate = this.animate.bind(this);
        this.resize = this.resize.bind(this);
        this.state = {
            gameState: new Game(this),
            isAskingToSpectate: false,
            hasAskedToSpectate: false,
            isBuilding: false,
        };
    }

    private setupPixi(): void {
        this.app = new PIXI.Application({
            transparent: true,
            autoResize: true,
            resolution: devicePixelRatio,
        });
        this.stage = this.app.stage;
        this.renderer = this.app.renderer;

        this.rendererPlugins().interaction.cursorStyles['crosshair'] = 'crosshair';
        this.rendererPlugins().interaction.cursorStyles['hide'] = 'none';

        window.addEventListener('resize', this.resize, true);
        window.addEventListener('onorientationchange', this.resize, true);

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.gameCanvas!.appendChild(this.renderer.view);

        const worldSize = this.worldSize();
        this.viewport = new Viewport({
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            worldWidth: worldSize.x,
            worldHeight: worldSize.y,
            interaction: this.app.renderer.plugins.interaction,
        })
            .drag()
            .clamp(this.clampOptions());

        this.viewport.on('clicked', this.onClick);
        this.viewport.on('mousemove', this.onMouseMove);
        this.viewport.on('moved', this.onViewportMove);

        MapRenderer.setupContainers(this.stage, this.viewport);
        MapRenderer.renderMap(this.state.gameState.map);
        HudRenderer.setup(GameModel.uiContainer);
        HudRenderer.startGame(this.state.gameState);

        pointerRenderer.setup();

        this.pixiIsSetup = true;
    }

    public componentDidMount(): void {
        this.finishSetup();
    }

    public componentWillUnmount(): void {
        this.gameCanvas = undefined;
        this.pixiIsSetup = false;
        if (this.pixiIsSetup) {
            this.renderer.destroy(true);
            this.viewport.destroy();
        }
        this.state.gameState.end();
        window.removeEventListener('resize', this.resize, true);
        window.removeEventListener('onorientationchange', this.resize, true);
    }

    public finishSetup(): void {
        if (!this.gameCanvas || !this.state.gameState.map) return;
        this.setupPixi();
        this.resize();
        this.animate();
        this.state.gameState.start();
        this.state.gameState.runServerActions([]);
    }

    private get currentState(): State {
        return this.state.gameState.currentState;
    }

    public onClick = (_e: Viewport.ClickEventData) => {
        if (this.currentState) {
            this.currentState.onMouseClick(this.worldPointerPosition());
        }
    };

    public onMouseMove = (_e: PIXI.interaction.InteractionEvent) => {
        if (this.currentState) {
            this.currentState.onMouseMove(this.worldPointerPosition());
        }
    };

    public onViewportMove = (_e: Viewport) => {
        if (this.currentState) {
            this.currentState.onMouseMove(this.worldPointerPosition());
        }
    };

    private animate(): void {
        if (!this.pixiIsSetup) return;
        if (this.currentState) {
            this.currentState.tick();
        }
        pointerRenderer.tick();
        buildabilityRenderer.tick();
        this.renderer.render(this.stage);
        if (this.pixiIsSetup) {
            requestAnimationFrame(this.animate);
        }
    }

    public render(): JSX.Element {
        return (
            <div className="full-size">
                <div ref={this.updateContainer} className="frame full-size" />
                {this.state.isAskingToSpectate && (
                    <div className="modal ask-spectate">
                        <p>Spectate?</p>
                        <button type="button" onClick={this.spectate}>
                            Spectate
                        </button>
                        <button type="button" onClick={this.returnToLobby}>
                            Return to lobby
                        </button>
                    </div>
                )}
                {!this.state.isAskingToSpectate && this.state.hasAskedToSpectate && (
                    <div className="modal return-to-lobby">
                        <button type="button" onClick={this.returnToLobby}>
                            Return to lobby
                        </button>
                    </div>
                )}
                {this.state.isBuilding && this.isMobile && (
                    <div className="modal turn-options">
                        <button type="button" onClick={this.rotateWallPieceCounterclockwise}>
                            ↺
                        </button>
                        <button type="button" onClick={this.rotateWallPieceClockwise}>
                            ↻
                        </button>
                    </div>
                )}
            </div>
        );
    }

    private updateContainer = (element: HTMLDivElement) => {
        this.gameCanvas = element;
        if (this.gameCanvas && this.gameCanvas.childElementCount === 0 && this.renderer) {
            this.gameCanvas.appendChild(this.renderer.view);
        }
    };

    public onChangeGameState(): void {
        if (this.renderer) {
            if (this.currentState) {
                this.currentState.onMouseMove(this.worldPointerPosition());
            }
        }
    }

    public worldPointerPosition(): Point {
        return this.isMobile
            ? this.viewport.center.clone()
            : this.viewport.toWorld(this.rendererPlugins().interaction.mouse.global);
    }

    public worldPointerLocation(): Location {
        return Location.fromPoint(this.worldPointerPosition());
    }

    public showCursor(style: string = 'default'): void {
        this.stage.cursor = style;
    }

    public hideCursor(): void {
        this.stage.cursor = 'hide';
    }

    public askToSpectate(): void {
        this.setState({...this.state, isAskingToSpectate: true, hasAskedToSpectate: true});
    }

    private spectate = () => {
        this.setState({...this.state, isAskingToSpectate: false});
    };

    private returnToLobby = () => {
        this.props.onReturnToLobby();
    };

    private rendererPlugins(): PIXI.RendererPlugins {
        return ((this.renderer as unknown) as RendererWithPlugins).plugins;
    }

    private resize(): void {
        const worldSize = this.worldSize();
        this.viewport.resize(window.innerWidth, window.innerHeight, worldSize.x, worldSize.y);
        this.viewport.clamp(this.clampOptions());
        this.app.renderer.resize(window.innerWidth, window.innerHeight);
        HudRenderer.reflow(window.innerWidth, window.innerHeight);
    }

    private scrollOverflow(): Point {
        return this.isMobile
            ? new Point(window.innerWidth / 2, window.innerHeight / 2)
            : new Point(GameConfig.tileSize, GameConfig.tileSize);
    }

    public componentDidUpdate(_prevProps: Props, _prevState: PixiState): void {
        this.state.gameState.runServerActions(_prevProps.actions);
    }

    public enterBuildState(): void {
        this.setState({...this.state, isBuilding: true});
    }

    public exitBuildState(): void {
        this.setState({...this.state, isBuilding: false});
    }

    private rotateWallPieceCounterclockwise = () => {
        (this.state.gameState.currentState as BuildState).rotateWallPieceCounterclockwise();
    };

    private rotateWallPieceClockwise = () => {
        (this.state.gameState.currentState as BuildState).rotateWallPieceClockwise();
    };

    public get isMobile(): boolean {
        return PIXI.utils.isMobile.any;
    }

    private worldSize(): Point {
        const {map} = this.state.gameState;
        const overflow = this.scrollOverflow();
        return new Point(
            map.width * GameConfig.tileSize + overflow.x * 2,
            map.height * GameConfig.tileSize + overflow.y * 2
        );
    }

    private clampOptions(): Viewport.ClampOptions {
        const {map} = this.state.gameState;
        const overflow = this.scrollOverflow();
        return {
            left: -overflow.x,
            top: -overflow.y,
            right: map.width * GameConfig.tileSize + overflow.x,
            bottom: map.height * GameConfig.tileSize + overflow.y,
        };
    }
}

export default PixiCanvas;



// WEBPACK FOOTER //
// ./src/Pixi/index.tsx